import { ELanguage, MICROSERVICE_DOMAIN, VNW_SERECT_KEY } from '@/common'
import { TMeta, TResponse } from '../index'
import ModelError from '../model/error'
import {
    COMPETITIVE_RATE_ENDPOINT,
    INCREASE_JOB_VIEW_ENDPOINT,
    JOBS_DETAIL_ENDPOINT,
    JOBS_SEARCH_ENDPOINT,
    JOBS_SPECIALS_INFO_ENDPOINT,
    JOB_ENDPOINT,
    MATCHING_JOB_ENDPOINT,
    SAVE_JOB_ENDPOINT,
    SUCCESS_CODE,
    UN_SAVE_JOB_ENDPOINT,
    VIEW_JOB_ENDPOINT,
    UPDATE_JOB_INVITATION_ENDPOINT
} from './const'
import {
    IJobDetail,
    TJob,
    TJobSpecialInfo,
    ESaveJob,
    TWinRate,
    TReport,
    TResponseMatchingJob,
    TParams,
    TUpdateJobInvitationRequest
} from './type'
import { encodeToken, getFilterQueryJobsSeach } from './helper'
import { TUTMTracking } from '@/server'
import { parseJSON } from '@/helpers'
import {
    DEFAULT_COMPANY_LOGO,
    DEFAULT_COMPANY_PROFILE_BANNER
} from '@/components/company/const'

export const getSpecialJob = async (
    jobIds: number[],
    accessToken: string,
    langCode?: ELanguage
): Promise<Partial<TJobSpecialInfo>[] | ModelError> => {
    try {
        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${JOBS_SPECIALS_INFO_ENDPOINT}`,
            {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': langCode ?? ELanguage.VI,
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({ jobId: jobIds })
            }
        )
        const responseData: TResponse<Partial<TJobSpecialInfo>[]> =
            await response.json()

        if (!responseData.data) {
            return new ModelError({
                data: null,
                error: {
                    details: {
                        ERROR: responseData?.error ?? ''
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        const data = responseData.data

        return data
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const getOtherJobsInCompanyId = async (
    companyId: number,
    excludeJobId: number,
    userId: string | null,
    langCode?: ELanguage,
    retrieveFields?: string[],
    limit?: number
): Promise<Partial<TJob>[] | ModelError> => {
    try {
        const body = getFilterQueryJobsSeach(
            companyId,
            excludeJobId,
            userId,
            retrieveFields,
            limit
        )
        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${JOBS_SEARCH_ENDPOINT}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': langCode ?? ELanguage.VI,
                    'X-Source': 'Job-Details'
                },
                body: JSON.stringify(body)
            }
        )
        const responseData: TResponse<Partial<TJob>[]> = await response.json()

        if (!responseData.data) {
            return new ModelError({
                data: null,
                error: {
                    details: {
                        ERROR: responseData?.error ?? ''
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        const data = responseData.data
        return data
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const getJobDetail = async (
    params: TParams
): Promise<Partial<IJobDetail> | ModelError> => {
    try {
        const {
            jobId,
            allowExpired = '0',
            userId,
            langCode,
            allowInactive = '0',
            allowIncomplete = '0',
            preview = '0'
        } = params
        const apiToken = encodeToken(String(jobId) || '', VNW_SERECT_KEY)

        let queryParams = `jobId=${jobId}&onlineOn=true`
        if (allowExpired !== '0') {
            queryParams += `&allowExpired=${allowExpired}`
        }
        if (allowInactive !== '0') {
            queryParams += `&allowInactive=${allowInactive}`
        }
        if (allowIncomplete !== '0') {
            queryParams += `&allowIncomplete=${allowIncomplete}`
        }
        if (preview !== '0') {
            queryParams += `&preview=${preview}`
        }
        if (userId) {
            queryParams += `&userId=${userId}`
        }

        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${JOBS_DETAIL_ENDPOINT}?${queryParams}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    apiToken: apiToken,
                    'Accept-Language': langCode ?? ELanguage.VI
                }
            }
        )

        const responseData: TResponse<IJobDetail> = await response.json()
        if (!responseData?.data) {
            return new ModelError({
                data: null,
                error: {
                    details: {
                        ERROR: responseData?.error ?? ''
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        const job = responseData.data
        const services = job?.services?.map((service) => {
            return service.serviceCode
        })

        const formatJob: IJobDetail = {
            ...job,
            isPremiumJob: !job.simpleServices
                ? false
                : job?.simpleServices?.includes('isPremium'),
            isHeadHuntJob: !job?.simpleServices
                ? false
                : job?.simpleServices?.includes('isHeadhunt'),
            isTopManagementJob: !services ? false : services?.includes('TMJ')
        }
        if (job.isAnonymous === true) {
            return {
                ...formatJob,
                companyInfo: {
                    ...formatJob.companyInfo,
                    companyName: '',
                    companyId: 0,
                    companyLogoURL: '',
                    bannerDesktopUri: DEFAULT_COMPANY_PROFILE_BANNER,
                    companyProfileBannerUrl: DEFAULT_COMPANY_PROFILE_BANNER,
                    companyProfile: '',
                    companyProfileUrl: '',
                    address: '',
                    companySizeId: '',
                    contactEmail: '',
                    contactName: '',
                    website: '',
                    profileBaseName: '',
                    jobImageURLs: [],
                    jobVideoURLs: []
                },
                companyLogo: DEFAULT_COMPANY_LOGO,
                companySize: '',
                companySizeVI: '',
                contactName: '',
                address: '',
                companyProfile: '',
                emailAddress: ''
            }
        }
        return formatJob
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const saveJob = async (
    jobId: number,
    accessToken: string,
    type: ESaveJob,
    langCode: ELanguage
) => {
    try {
        const enpoint =
            type === ESaveJob.save ? SAVE_JOB_ENDPOINT : UN_SAVE_JOB_ENDPOINT

        const response = await fetch(`${MICROSERVICE_DOMAIN}/${enpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': langCode ?? ELanguage.VI,
                Authorization: `Bearer ${accessToken}`
            },
            body: JSON.stringify({ jobId })
        })
        const responseData: TResponse<{ id: number; type: string }> =
            await response.json()
        if (responseData.meta?.code !== SUCCESS_CODE) {
            return new ModelError({
                data: responseData.data,
                error: {
                    details: {
                        ERROR: responseData?.error ?? ''
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }

        return responseData.data
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const getWinRateScore = async (
    jobId: number,
    accessToken: string,
    langCode: ELanguage
): Promise<ModelError | Partial<TWinRate>> => {
    try {
        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${COMPETITIVE_RATE_ENDPOINT}?jobId=${jobId}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': langCode ?? ELanguage.VI,
                    Authorization: `Bearer ${accessToken}`
                }
            }
        )
        const responseData: Partial<TWinRate> = await response.json()
        if (!responseData.result?.score) {
            return new ModelError({
                data: responseData,
                error: {
                    details: {
                        ERROR: 'Error'
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }

        return responseData
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const viewJob = async (accessToken: string, jobId: number) => {
    try {
        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${VIEW_JOB_ENDPOINT}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({ jobId })
            }
        )
        const responseData: Partial<{ status: TMeta }> = await response.json()
        if (!responseData.status) {
            return new ModelError({
                data: responseData,
                error: {
                    details: {
                        ERROR: 'Error'
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        return responseData
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const getReportJobInfo = async (accessToken: string, jobId: number) => {
    try {
        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${JOB_ENDPOINT}/${jobId}/report`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                }
            }
        )
        const responseData: Partial<{ data: TReport }> = await response.json()
        if (!responseData.data) {
            return new ModelError({
                data: responseData,
                error: {
                    details: {
                        ERROR: 'Error'
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        return responseData.data
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const increaseJobView = async (
    userId?: string,
    jobId?: number,
    langCode?: ELanguage,
    queryString?: string,
    utmTrackingGA?: string,
    utmTrackingGAFirst?: string,
    userIp?: string[]
) => {
    try {
        const formData = new FormData()

        if (userId) {
            formData.append('userId', userId || '')
        }

        if (jobId) {
            formData.append('jobId', String(jobId || ''))
        }

        if (userIp && userIp.length > 0) {
            formData.append('client_ip', userIp.join(','))
        }
        // append value in query params to form data

        const paramsObj = Object.fromEntries(new URLSearchParams(queryString))

        for (const [key, value] of Object.entries(paramsObj ?? {})) {
            if (value) {
                formData.append(key.replace('"', ''), value.replace('"', ''))
            }
        }

        const utmGA = parseJSON<TUTMTracking>(utmTrackingGA)

        for (const [key, value] of Object.entries(utmGA ?? {})) {
            const keyField = formData.has(key)
            if (keyField) {
                continue
            } else {
                formData.append(key, value || '')
            }
        }

        const utmGAFirst = parseJSON<TUTMTracking>(utmTrackingGAFirst)

        for (const [key, value] of Object.entries(utmGAFirst ?? {})) {
            const keyField = formData.has(`${key}_first`)

            if (keyField) {
                continue
            } else {
                formData.append(`${key}_first`, value || '')
            }
        }

        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${INCREASE_JOB_VIEW_ENDPOINT}`,
            {
                method: 'POST',
                headers: {
                    'Accept-Language': langCode ?? ELanguage.VI
                },
                body: formData
            }
        )
        const responseData: Partial<{ status: TMeta }> = await response.json()

        if (!responseData.status) {
            return new ModelError({
                data: responseData,
                error: {
                    details: {
                        ERROR: 'Error'
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        return responseData
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const matchingJob = async (
    accessToken: string,
    jobId: number,
    langCode: ELanguage
) => {
    try {
        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${MATCHING_JOB_ENDPOINT}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': langCode,
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({ jobId: jobId })
            }
        )
        const responseData: Partial<{ data: TResponseMatchingJob }> =
            await response.json()

        if (!responseData.data) {
            return new ModelError({
                data: responseData,
                error: {
                    details: {
                        ERROR: 'Error'
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }
        return responseData.data
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const updateJobInvitation = async (
    params: TUpdateJobInvitationRequest
) => {
    try {
        const response = await fetch(
            `${MICROSERVICE_DOMAIN}/${UPDATE_JOB_INVITATION_ENDPOINT}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': params.langCode ?? ELanguage.VI,
                    Authorization: `Bearer ${params.accessToken}`
                },
                body: JSON.stringify({
                    jobInvitationId: params.jobInvitationId,
                    jobId: params.jobId
                })
            }
        )
        const responseData: TResponse<{ id: number; type: string }> =
            await response.json()

        if (responseData.meta?.code !== SUCCESS_CODE) {
            return new ModelError({
                data: responseData.data,
                error: {
                    details: {
                        ERROR: responseData?.error ?? ''
                    },
                    message: response.statusText,
                    status: response.status
                }
            })
        }

        return responseData.data
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const getJobInvitation = async (params: TUpdateJobInvitationRequest) => {
    try {
        const endpoint = `${MICROSERVICE_DOMAIN}/${UPDATE_JOB_INVITATION_ENDPOINT}?jobId=${params.jobId}`

        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': params.langCode ?? ELanguage.VI,
                Authorization: `Bearer ${params.accessToken}`
            }
        })
        const responseData: TResponse<{
            id: number
            status: number
            attributes: {
                jobInvitationId: number
                status: number
                content: string
            }
        }> = await response.json()

        return responseData.data
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const apiGetResumeStatusList = async (
    accessToken: string,
    langCode: ELanguage
) => {
    try {
        const apiUrl = MICROSERVICE_DOMAIN + '/meta/v1.0/application-status'
        const getResumeStatusResponse = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
                'Accept-Language': langCode
            }
        })
        return await getResumeStatusResponse.json()
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}

export const apiGetListJobStatus = async (
    accessToken: string,
    jobIdList: number[],
    langCode: ELanguage
) => {
    try {
        let apiUrl =
            MICROSERVICE_DOMAIN + '/api-gateway/v1.0/application-status'
        if (jobIdList.length > 0) {
            apiUrl += `?jobId=${jobIdList.join(',')}`
        }
        const getResumeStatusResponse = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
                'Accept-Language': langCode
            }
        })

        return await getResumeStatusResponse.json()
    } catch (error) {
        throw new ModelError(error as ModelError)
    }
}
